import { Alert, Button, Card, Progress, Spin, Typography } from "antd";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import NudgeLogo from "../../components/NudgeLogo";
import { useIsMobileView } from "../../hooks/ui-hook";
import {
  type SetupPortal as Portal,
  type StepKey,
  isGated,
  useGetSetupPortalQuery,
  useVerifySetupAccessMutation,
} from "../../service/implementation.service";
import { takeAccessToken } from "../../utils/portalAccessToken";
import AccessGate from "./AccessGate.component";
import BaaStep from "./BaaStep.component";
import EhrLoginForm from "./EhrLoginForm.component";
import GoLiveStep from "./GoLiveStep.component";
import GroupAdminForm from "./GroupAdminForm.component";
import SetupSteps from "./SetupSteps.component";
import styles from "./setup.module.scss";

const { Text, Title } = Typography;

/** Banner kind onto Ant's Alert types, so it renders in the app's own styling. */
const BANNER_TYPE = {
  action_needed: "warning",
  nudge_working: "info",
  complete: "success",
} as const;

/**
 * Page chrome, wrapped around every state — loading, error, gate and tracker —
 * so the customer sees real Nudge branding from the first paint. `showDomain` is
 * off because a group at this stage hasn't picked a practice area yet.
 */
const PortalShell = ({ children }: { children: ReactNode }) => (
  <div className={styles.page}>
    <header className={styles.header}>
      <NudgeLogo showDomain={false} />
    </header>
    <div className={styles.body}>{children}</div>
  </div>
);

/**
 * The customer-facing implementation progress portal ("onboarding portal").
 *
 * Reached at /setup/:token — outside AuthLayout, because at the moment payment
 * clears nobody at the practice has a Nudge login yet. Access is granted by the
 * magic link in ?access=, which we redeem and then strip from the URL.
 */
export default function SetupPortalPage() {
  const { token = "" } = useParams<{ token: string }>();
  const isMobile = useIsMobileView();
  const [searchParams, setSearchParams] = useSearchParams();
  // Already stripped from the URL before analytics loaded, so it comes from memory.
  // useState so a re-render doesn't consume it twice.
  const [accessToken] = useState(() => takeAccessToken());
  // DocuSign returns the customer here after signing.
  const returnedFromSigning = searchParams.get("baa") === "returned";

  const [verify, { isLoading: isVerifying }] = useVerifySetupAccessMutation();
  const [linkError, setLinkError] = useState<string | undefined>();
  const [openStep, setOpenStep] = useState<StepKey | null>(null);
  const [hasTriedLink, setHasTriedLink] = useState(!accessToken);

  const { data, isLoading, isError, refetch } = useGetSetupPortalQuery(token, {
    skip: !token,
  });

  // Seed the CSRF cookie before any write. Every POST here is csrf_protect'd, and
  // a customer arriving straight from their email has never hit /login or
  // /register, so nothing has set the cookie yet — the portal GET doesn't either.
  //
  // This has to be awaited rather than fired and forgotten: the magic-link redeem
  // below runs in the same commit phase, and without the cookie Django returns 403,
  // which the redeem's catch would report to the customer as an expired link on
  // their very first click. One shared promise, so both the redeem and any later
  // form submit wait on the same request.
  const csrfReadyRef = useRef<Promise<void> | null>(null);
  if (csrfReadyRef.current === null) {
    csrfReadyRef.current = fetch(`${process.env.REACT_APP_SERVER_URL}/users/csrf/`, {
      credentials: "include",
    })
      .then(() => undefined)
      // Swallow failures: a submit will surface its own error rather than
      // blocking the page from rendering.
      .catch(() => undefined);
  }

  // Redeem the magic link. It was already removed from the address bar (and from
  // history) by utils/portalAccessToken before any analytics SDK loaded.
  //
  // The ref guard matters: the grant is single-use, so a second run of this effect
  // would burn it and show the customer an "already used" error on their first
  // visit. Reading the query string off window.location instead of `searchParams`
  // keeps that object out of the dependency list, which is what would re-trigger it.
  const redeemedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!accessToken || !token) return;
    if (redeemedRef.current === accessToken) return;
    redeemedRef.current = accessToken;
    let cancelled = false;
    (async () => {
      try {
        // Wait for the CSRF cookie, or this POST 403s and we would tell the
        // customer their brand-new link had expired.
        await csrfReadyRef.current;
        await verify({ token, accessToken }).unwrap();
      } catch {
        if (!cancelled) {
          setLinkError("That link has expired or has already been used. Enter your email for a fresh one.");
        }
      } finally {
        if (!cancelled) setHasTriedLink(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, token, verify]);

  // Strip ?baa= once handled, so a refresh doesn't re-run the confirmation.
  const clearSigningReturn = useCallback(() => {
    const next = new URLSearchParams(window.location.search);
    next.delete("baa");
    setSearchParams(next, { replace: true });
  }, [setSearchParams]);

  const portal = !isGated(data) ? (data as Portal | undefined) : undefined;

  // Open the step that needs them on first paint, so the page lands on the thing to
  // act on. Once only: keyed on `openStep === null` it re-fired the moment the
  // customer collapsed a step, snapping it straight back open — which read as
  // collapse being broken.
  const didAutoOpenRef = useRef(false);
  useEffect(() => {
    if (portal && !didAutoOpenRef.current) {
      didAutoOpenRef.current = true;
      setOpenStep(portal.banner.step_key ?? portal.progress.current_step_key ?? null);
    }
  }, [portal]);

  const stepBody = useMemo(
    () => ({
      baa: portal ? (
        <BaaStep
          token={token}
          portal={portal}
          justReturned={returnedFromSigning}
          onReturnHandled={clearSigningReturn}
        />
      ) : null,
      group_admin: portal ? (
        <GroupAdminForm
          token={token}
          portal={portal}
          isComplete={!!portal.steps.find((s) => s.key === "group_admin" && s.status === "complete")}
        />
      ) : null,
      ehr_login: portal ? <EhrLoginForm token={token} portal={portal} /> : null,
      templates: null,
      ai_assistant: null,
      go_live: portal ? <GoLiveStep token={token} portal={portal} /> : null,
    }),
    [portal, token, returnedFromSigning, clearSigningReturn],
  );

  if (!token) {
    return (
      <PortalShell>
        <div className={styles.shell}>
          <Alert type="error" showIcon message="This onboarding link is not valid." />
        </div>
      </PortalShell>
    );
  }

  if ((isLoading || isVerifying || !hasTriedLink) && !portal) {
    return (
      <PortalShell>
        <div className={styles.centered}>
          <Spin size="large" />
          <span>Loading your onboarding…</span>
        </div>
      </PortalShell>
    );
  }

  if (isError && !data) {
    return (
      <PortalShell>
        <div className={styles.shell}>
          <Alert
            type="error"
            showIcon
            message="We couldn't open your onboarding"
            description="The link may be out of date. Try again, or reply to your welcome email and we'll send a fresh one."
            action={<Button onClick={() => refetch()}>Retry</Button>}
          />
        </div>
      </PortalShell>
    );
  }

  if (!portal) {
    return (
      <PortalShell>
        <AccessGate token={token} groupName={isGated(data) ? data.group_name : ""} linkError={linkError} />
      </PortalShell>
    );
  }

  const { banner, progress } = portal;

  return (
    <PortalShell>
      <div className={styles.shell}>
        <Alert
          className={styles.banner}
          type={BANNER_TYPE[banner.kind]}
          showIcon
          message={<span className={styles.bannerHeadline}>{banner.headline}</span>}
          description={<span className={styles.bannerBody}>{banner.body}</span>}
          action={
            banner.step_key ? (
              <Button
                type="primary"
                size={isMobile ? "middle" : "small"}
                block={isMobile}
                onClick={() => {
                  setOpenStep(banner.step_key);
                  document.getElementById("setup-steps")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              >
                Go to this step
              </Button>
            ) : null
          }
        />

        <Card
          className={styles.card}
          variant="borderless"
          styles={{ body: { padding: isMobile ? "16px 12px 4px" : "22px 24px 8px" } }}
        >
          <div className={styles.cardHead}>
            <Title level={5} className={styles.groupName}>
              {portal.group_name}
            </Title>
            <Text type="secondary" className={styles.progressLabel}>
              {progress.is_complete ? "All steps complete" : `Step ${progress.current_position} of ${progress.total}`}
              {" \u00b7 "}
              {progress.completed} of {progress.total} done
            </Text>
          </div>

          <Progress
            percent={Math.round((progress.completed / Math.max(progress.total, 1)) * 100)}
            showInfo={false}
            strokeColor={progress.is_complete ? "#10b981" : "#6366F1"}
            trailColor="#EEF2FF"
            size="small"
            className={styles.progressBar}
          />

          <div id="setup-steps">
            <SetupSteps
              steps={portal.steps}
              openStep={openStep}
              onToggle={(key) => setOpenStep(openStep === key ? null : key)}
              bodies={stepBody}
            />
          </div>
        </Card>

        {/* The support address used to sit here as issue §5's "escape hatch". Removed
            deliberately: surfacing it on every step invited contact for things the
            page already answers. A genuinely stuck customer still has a route — the
            blocked state names what to fix and reopens the form inline. */}
        <div className={styles.help}>
          <span className={styles.viewer}>Signed in as {portal.viewer_email}</span>
        </div>
      </div>
    </PortalShell>
  );
}
